import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Loader from '../common/Loader.js';
import { DashboardLayout } from '../common/page_component/index.js';
import Alert from '../common/Alert.js';
import companyService from '../../services/CompanyService.js';
import companySubscriptionService from '../../services/CompanySubscriptionService.js';
import { Building2, Pencil, ArrowLeft, Mail, Phone, MapPin, Calendar, FileText, CheckCircle2, XCircle, CreditCard, Plus, Eye, Network } from "lucide-react";
import Button from '../common/Button.js';
import { useTranslation } from 'react-i18next';
import {
  CORE_FEATURES,
  AGENT_NETWORK_FEATURES,
  formatFeatureLabel,
  mergeCanAccess,
  NETWORK_MODE_LABELS,
} from '../../utils/companyFeatures.js';
import CompanyIntegrationViewPanels from './CompanyIntegrationViewPanels.js';

const ViewCompany = () => {
  const { t } = useTranslation();
  const { companyId } = useParams();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscriptions, setSubscriptions] = useState([]);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);
  const [invoicing, setInvoicing] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCompany();
    fetchSubscriptions();
  }, [companyId]);

  const fetchCompany = async () => {
    try {
      const response = await companyService.getById(companyId);
      setCompany(response.data);
    } catch (err) {
      Alert.error('Failed to load company details');
      console.error(err);
      navigate('/companies');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptions = async () => {
    try {
      setSubscriptionsLoading(true);
      const response = await companySubscriptionService.getByCompany(companyId, true);
      setSubscriptions(response.data || []);
    } catch (err) {
      console.error('Error fetching subscriptions:', err);
    } finally {
      setSubscriptionsLoading(false);
    }
  };

  const handleDownloadInvoice = async () => {
    if (!company?.id || invoicing) return;
    try {
      setInvoicing(true);
      const { filename } = await companyService.downloadBillingInvoice(company.id);
      Alert.success(`Downloaded ${filename}`);
    } catch (err) {
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const payload = JSON.parse(text);
          Alert.error(payload.error || 'Failed to download invoice');
        } catch {
          Alert.error('Failed to download invoice');
        }
      } else {
        Alert.error(err.response?.data?.error || 'Failed to download invoice');
      }
    } finally {
      setInvoicing(false);
    }
  };

  const handleEmailInvoice = async () => {
    if (!company?.id || emailing) return;
    if (!company.email) {
      Alert.error('Company has no email address');
      return;
    }
    const confirmed = await Alert.confirm(
      `Send dues invoice to ${company.email}?`,
      'Email Invoice'
    );
    if (!confirmed) return;
    try {
      setEmailing(true);
      const res = await companyService.sendBillingInvoiceEmail(company.id);
      Alert.success(res.message || `Invoice emailed to ${company.email}`);
    } catch (err) {
      Alert.error(err.response?.data?.error || err.response?.data?.message || 'Failed to email invoice');
    } finally {
      setEmailing(false);
    }
  };

  const getLogoUrl = () => {
    return company?.logo_path || null;
  };

  const getSignUrl = () => {
    return company?.sign_path || null;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatShortDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const isActive = status?.toLowerCase() === 'active';
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${
        isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
      }`}>
        {isActive ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
        {status?.charAt(0).toUpperCase() + status?.slice(1).toLowerCase()}
      </span>
    );
  };

  const getSubscriptionStatusBadge = (isActive, isExpired) => {
    if (isActive) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
          <CheckCircle2 className="w-3 h-3" />
          Active
        </span>
      );
    } else if (isExpired) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
          <XCircle className="w-3 h-3" />
          Expired
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
          <XCircle className="w-3 h-3" />
          Inactive
        </span>
      );
    }
  };

  const getValidityStatus = () => {
    if (!company?.from_date || !company?.to_date) return null;
    
    const today = new Date();
    const fromDate = new Date(company.from_date);
    const toDate = new Date(company.to_date);
    
    if (today < fromDate) {
      return { status: 'upcoming', label: 'Upcoming', color: 'bg-blue-100 text-blue-700' };
    } else if (today >= fromDate && today <= toDate) {
      return { status: 'valid', label: 'Valid', color: 'bg-green-100 text-green-700' };
    } else {
      return { status: 'expired', label: 'Expired', color: 'bg-red-100 text-red-700' };
    }
  };

  const validityStatus = company ? getValidityStatus() : null;
  const canAccess = company ? mergeCanAccess(company.can_access) : {};
  const isExternalNetwork = company?.network_mode === 'EXTERNAL';

  const renderFeatureCards = (features) => (
    features.map((feature) => {
      const forcedOff = isExternalNetwork && feature === 'attendance';
      const enabled = forcedOff ? false : Boolean(canAccess[feature]);
      const attendanceMethods = feature === 'attendance' && enabled
        ? [
            canAccess.attendance_manual && 'Manual',
            canAccess.attendance_qr && 'QR',
          ].filter(Boolean)
        : [];
      return (
        <div
          key={feature}
          className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
            enabled ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-300'
          }`}
        >
          {enabled ? (
            <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
          ) : (
            <XCircle className="w-5 h-5 text-gray-400 flex-shrink-0" />
          )}
          <div>
            <p className={`text-sm font-semibold ${enabled ? 'text-green-900' : 'text-gray-600'}`}>
              {formatFeatureLabel(feature)}
            </p>
            <p className={`text-xs ${enabled ? 'text-green-700' : 'text-gray-500'}`}>
              {forcedOff
                ? 'Not available in Agent mode'
                : feature === 'attendance' && enabled
                  ? (attendanceMethods.length ? attendanceMethods.join(' · ') : 'No login method selected')
                  : enabled ? 'Enabled' : 'Disabled'}
            </p>
          </div>
        </div>
      );
    })
  );

  return (
    <DashboardLayout>
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading company details..." />
        </div>
      ) : !company ? null : (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                {/* Header Section */}
                <div className="p-4 sm:p-6 border-b border-gray-200">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <Button variant="ghost" onClick={() => navigate('/companies')} title={t('actions.backToCompanies')}>
                        <ArrowLeft className="w-5 h-5 text-gray-600" />
                      </Button>
                      <div className="p-2 bg-orange-100 rounded-xl">
                        <Building2 className="w-6 h-6 text-orange-600" />
                      </div>
                      <div>
                        <h2 className="text-xl sm:text-2xl font-bold text-gray-700">Company Details</h2>
                        <p className="text-xs sm:text-sm text-gray-600 mt-1">View complete company information</p>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                      <Button
                        variant="secondary"
                        icon={FileText}
                        onClick={handleDownloadInvoice}
                        disabled={invoicing || emailing}
                      >
                        {invoicing ? 'Invoice...' : 'Invoice'}
                      </Button>
                      <Button
                        variant="secondary"
                        icon={Mail}
                        onClick={handleEmailInvoice}
                        disabled={invoicing || emailing}
                      >
                        {emailing ? 'Emailing...' : 'Email Invoice'}
                      </Button>
                      <Button variant="primary" icon={Pencil} onClick={() => navigate(`/companies/edit/${companyId}`)}>
                        Edit Company
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Main Content */}
                <div className="p-4 sm:p-6 space-y-6">
                  {/* Company Overview Card */}
                  <div className="bg-gradient-to-r from-orange-50 to-white border border-orange-100 rounded-xl p-6">
                    <div className="flex flex-col md:flex-row gap-6">
                      {/* Logo Section */}
                      <div className="flex-shrink-0 flex gap-3">
                        {getLogoUrl() ? (
                          <img
                            src={getLogoUrl()}
                            alt={company.company_name}
                            className="w-32 h-32 object-cover border-2 border-orange-200 rounded-md shadow-sm"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        ) : (
                          <div className="w-32 h-32 bg-orange-100 border-2 border-orange-200 rounded-md flex items-center justify-center">
                            <Building2 className="w-16 h-16 text-orange-400" />
                          </div>
                        )}
                        {getSignUrl() ? (
                          <div>
                            <img
                              src={getSignUrl()}
                              alt="Authorised signature"
                              className="w-32 h-32 object-contain border-2 border-orange-200 rounded-md shadow-sm bg-white p-2"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                            <p className="text-xs text-gray-500 mt-1 text-center">Signature</p>
                          </div>
                        ) : null}
                      </div>

                      {/* Company Info */}
                      <div className="flex-1 space-y-4">
                        <div>
                          <div className="flex flex-wrap items-center gap-3 mb-2">
                            <h3 className="text-2xl font-bold text-gray-900">{company.company_name}</h3>
                            {getStatusBadge(company.status)}
                            {validityStatus && (
                              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${validityStatus.color}`}>
                                {validityStatus.label}
                              </span>
                            )}
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              isExternalNetwork ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-700'
                            }`}>
                              {NETWORK_MODE_LABELS[company.network_mode] || 'Company Owner'} Mode
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 font-medium">Company ID: {company.company_id}</p>
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-2 gap-2 sm:gap-4">
                          <div className="flex items-start gap-2">
                            <Mail className="w-4 h-4 text-gray-500 mt-0.5" />
                            <div>
                              <p className="text-xs text-gray-500">Email</p>
                              <p className="text-sm font-medium text-gray-900">{company.email}</p>
                            </div>
                          </div>
                          <div className="flex items-start gap-2">
                            <Phone className="w-4 h-4 text-gray-500 mt-0.5" />
                            <div>
                              <p className="text-xs text-gray-500">Contact</p>
                              <p className="text-sm font-medium text-gray-900">{company.contact}</p>
                            </div>
                          </div>
                          {company.support_contact && (
                            <div className="flex items-start gap-2">
                              <Phone className="w-4 h-4 text-gray-500 mt-0.5" />
                              <div>
                                <p className="text-xs text-gray-500">Support Contact</p>
                                <p className="text-sm font-medium text-gray-900">{company.support_contact}</p>
                              </div>
                            </div>
                          )}
                          <div className="flex items-start gap-2 col-span-2 sm:col-span-2">
                            <MapPin className="w-4 h-4 text-gray-500 mt-0.5" />
                            <div>
                              <p className="text-xs text-gray-500">{t('common.address')}</p>
                              <p className="text-sm font-medium text-gray-900">
                                {company.address}, {company.city}, {company.state} - {company.pincode}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Business Information */}
                  <div>
                    <h3 className="text-base sm:text-lg font-semibold text-gray-700 mb-4 pb-2 border-b-2 border-gray-200 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-orange-600" />
                      Business Information
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-6">
                      <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                        <p className="text-xs text-gray-500 mb-1">Company Code</p>
                        <p className="text-sm font-semibold text-gray-900">{company.company_code || 'Not Provided'}</p>
                      </div>

                      <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                        <p className="text-xs text-gray-500 mb-1">Business Type</p>
                        <p className="text-sm font-semibold text-gray-900">{company.business_type || 'Not Provided'}</p>
                      </div>

                      <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                        <p className="text-xs text-gray-500 mb-1">GST Number</p>
                        <p className="text-sm font-semibold text-gray-900">{company.gst_number || 'Not Provided'}</p>
                      </div>

                      <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                        <p className="text-xs text-gray-500 mb-1">PAN Number</p>
                        <p className="text-sm font-semibold text-gray-900">{company.pan_number || 'Not Provided'}</p>
                      </div>

                      <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                        <p className="text-xs text-gray-500 mb-1">CIN/LLPIN</p>
                        <p className="text-sm font-semibold text-gray-900">{company.cin_llpin || 'Not Provided'}</p>
                      </div>

                      <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                        <p className="text-xs text-gray-500 mb-1">CST Number</p>
                        <p className="text-sm font-semibold text-gray-900">{company.cst_number || 'Not Provided'}</p>
                      </div>

                      <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                        <p className="text-xs text-gray-500 mb-1">Udyam/MSME Number</p>
                        <p className="text-sm font-semibold text-gray-900">{company.udyam_msme_number || 'Not Provided'}</p>
                      </div>

                      <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                        <p className="text-xs text-gray-500 mb-1">TAN Number</p>
                        <p className="text-sm font-semibold text-gray-900">{company.tan_number || 'Not Provided'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Banking Details */}
                  <div>
                    <h3 className="text-base sm:text-lg font-semibold text-gray-700 mb-4 pb-2 border-b-2 border-gray-200">Banking Details</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-6">
                      <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                        <p className="text-xs text-gray-500 mb-1">Bank Name</p>
                        <p className="text-sm font-semibold text-gray-900">{company.bank_name || 'Not Provided'}</p>
                      </div>

                      <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                        <p className="text-xs text-gray-500 mb-1">Branch Name</p>
                        <p className="text-sm font-semibold text-gray-900">{company.branch_name || 'Not Provided'}</p>
                      </div>

                      <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                        <p className="text-xs text-gray-500 mb-1">Account Type</p>
                        <p className="text-sm font-semibold text-gray-900">{company.account_type || 'Not Provided'}</p>
                      </div>

                      <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                        <p className="text-xs text-gray-500 mb-1">Account Name</p>
                        <p className="text-sm font-semibold text-gray-900">{company.account_name || 'Not Provided'}</p>
                      </div>

                      <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                        <p className="text-xs text-gray-500 mb-1">Account Number</p>
                        <p className="text-sm font-semibold text-gray-900">{company.account_number || 'Not Provided'}</p>
                      </div>

                      <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                        <p className="text-xs text-gray-500 mb-1">IFSC Code</p>
                        <p className="text-sm font-semibold text-gray-900">{company.ifsc_code || 'Not Provided'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Validity Period */}
                  <div>
                    <h3 className="text-base sm:text-lg font-semibold text-gray-700 mb-4 pb-2 border-b-2 border-gray-200 flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-orange-600" />
                      Validity Period
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-6">
                      <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
                        <p className="text-xs text-blue-600 mb-1 font-medium">From Date</p>
                        <p className="text-sm font-semibold text-blue-900">{formatDate(company.from_date)}</p>
                      </div>

                      <div className="bg-green-50 p-4 rounded-xl border border-green-200">
                        <p className="text-xs text-green-600 mb-1 font-medium">To Date</p>
                        <p className="text-sm font-semibold text-green-900">{formatDate(company.to_date)}</p>
                      </div>

                      <div className="bg-amber-50 p-4 rounded-xl border border-amber-200">
                        <p className="text-xs text-amber-700 mb-1 font-medium">User Limit</p>
                        <p className="text-sm font-semibold text-amber-900">
                          {company.user_limit != null ? company.user_limit : 'Unlimited'}
                        </p>
                        <p className="text-xs text-amber-700 mt-1">
                          {company.user_count ?? 0} user(s) created
                        </p>
                      </div>

                      <div className={`p-4 rounded-xl border-2 ${
                        validityStatus?.status === 'valid' ? 'bg-green-50 border-green-300' :
                        validityStatus?.status === 'expired' ? 'bg-red-50 border-red-300' :
                        'bg-blue-50 border-blue-300'
                      }`}>
                        <p className="text-xs mb-1 font-medium" style={{
                          color: validityStatus?.status === 'valid' ? '#15803d' :
                                 validityStatus?.status === 'expired' ? '#991b1b' :
                                 '#1e40af'
                        }}>Current Status</p>
                        <p className="text-sm font-semibold" style={{
                          color: validityStatus?.status === 'valid' ? '#15803d' :
                                 validityStatus?.status === 'expired' ? '#991b1b' :
                                 '#1e40af'
                        }}>{validityStatus?.label}</p>
                      </div>
                    </div>
                  </div>

                  {/* Agent Network Mode */}
                  <div>
                    <h3 className="text-base sm:text-lg font-semibold text-gray-700 mb-4 pb-2 border-b-2 border-gray-200 flex items-center gap-2">
                      <Network className="w-5 h-5 text-orange-600" />
                      Agent Network Mode
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-2 gap-2 sm:gap-6">
                      <div className={`p-4 rounded-xl border-2 ${
                        isExternalNetwork ? 'bg-purple-50 border-purple-300' : 'bg-slate-50 border-slate-300'
                      }`}>
                        <p className="text-xs text-gray-500 mb-1">Operation Mode</p>
                        <p className={`text-sm font-semibold ${isExternalNetwork ? 'text-purple-900' : 'text-slate-900'}`}>
                          {NETWORK_MODE_LABELS[company.network_mode] || 'Company Owner'}
                        </p>
                        <p className="text-xs text-gray-600 mt-2">
                          {isExternalNetwork
                            ? 'Cross-company agent partnerships and network shipment flows are available when Agent Network is enabled.'
                            : 'Standard Company Owner operations only. No cross-company agent network UI.'}
                        </p>
                      </div>
                      {isExternalNetwork && (
                        <div className={`p-4 rounded-xl border-2 ${
                          canAccess.agent_network ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-300'
                        }`}>
                          <p className="text-xs text-gray-500 mb-1">Agent Network Status</p>
                          <p className={`text-sm font-semibold ${canAccess.agent_network ? 'text-green-900' : 'text-gray-700'}`}>
                            {canAccess.agent_network ? 'Enabled' : 'Disabled'}
                          </p>
                          <p className="text-xs text-gray-600 mt-2">
                            {canAccess.agent_network
                              ? 'Users will see Agent Network menu items and external booking options.'
                              : 'Set Agent Network on to activate cross-company features for this company.'}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Core Feature Access */}
                  <div>
                    <h3 className="text-base sm:text-lg font-semibold text-gray-700 mb-4 pb-2 border-b-2 border-gray-200 flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-orange-600" />
                      Core Feature Access
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4">
                      {renderFeatureCards(CORE_FEATURES)}
                    </div>
                  </div>

                  <CompanyIntegrationViewPanels company={company} canAccess={canAccess} />

                  {isExternalNetwork && (
                    <div>
                      <h3 className="text-base sm:text-lg font-semibold text-gray-700 mb-4 pb-2 border-b-2 border-gray-200 flex items-center gap-2">
                        <Network className="w-5 h-5 text-orange-600" />
                        Agent Network Features
                      </h3>
                      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4">
                        {renderFeatureCards(AGENT_NETWORK_FEATURES)}
                      </div>
                    </div>
                  )}

                  {/* Subscriptions Section */}
                  <div>
                    <div className="flex items-center justify-between mb-4 pb-2 border-b-2 border-gray-200">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-700 flex items-center gap-2">
                        <CreditCard className="w-5 h-5 text-orange-600" />
                        Company Subscriptions ({subscriptions.length})
                      </h3>
                      <Button variant="primary" size="sm" icon={Plus} onClick={() => navigate(`/company-subscriptions/add?companyId=${company.id}`)}>
                        Add Subscription
                      </Button>
                    </div>

                    {subscriptionsLoading ? (
                      <div className="text-center py-8">
                        <Loader size="small" message="Loading subscriptions..." />
                      </div>
                    ) : subscriptions.length === 0 ? (
                      <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
                        <CreditCard className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">No subscriptions found</h3>
                        <p className="mt-1 text-sm text-gray-500">Create the first subscription for this company</p>
                        <div className="mt-6">
                          <Button variant="primary" icon={Plus} onClick={() => navigate(`/company-subscriptions/add?companyId=${company.id}`)}>
                            Add Subscription
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3 md:space-y-0 md:grid md:grid-cols-2 lg:grid-cols-3 md:gap-4">
                        {subscriptions.map((subscription) => (
                          <div
                            key={subscription.id}
                            className="bg-white border border-gray-200 rounded-xl p-3 md:p-4 hover:shadow-md transition-shadow cursor-pointer shadow-sm"
                            onClick={() => navigate(`/company-subscriptions/view/${subscription.id}`)}
                          >
                            <div className="flex items-start justify-between gap-2 mb-2 md:mb-3">
                              <div className="flex items-center gap-2 min-w-0">
                                <div className="p-1.5 md:p-2 bg-orange-100 rounded-xl shrink-0">
                                  <CreditCard className="w-4 h-4 text-orange-600" />
                                </div>
                                <div className="min-w-0">
                                  <p className="text-sm font-semibold text-gray-900 truncate">
                                    {subscription.subscription_id}
                                  </p>
                                  <p className="text-xs text-gray-500 truncate">
                                    {companySubscriptionService.getPeriodDisplayName(
                                      subscription.period_type,
                                      subscription.custom_years
                                    )}
                                  </p>
                                </div>
                              </div>
                              {getSubscriptionStatusBadge(subscription.is_active, subscription.is_expired)}
                            </div>

                            <div className="grid grid-cols-2 gap-2 md:space-y-2 md:block text-xs">
                              <div className="md:flex md:justify-between md:items-center">
                                <span className="text-gray-500">Period:</span>
                                <span className="font-medium text-gray-900 block md:inline">
                                  {formatShortDate(subscription.from_date)} - {formatShortDate(subscription.to_date)}
                                </span>
                              </div>

                              <div className="md:flex md:justify-between md:items-center">
                                <span className="text-gray-500">Amount:</span>
                                <span className="font-semibold text-orange-600 block md:inline">
                                  {formatCurrency(subscription.subscription_amount)}
                                </span>
                              </div>

                              <div className="md:flex md:justify-between md:items-center">
                                <span className="text-gray-500">Paid:</span>
                                <span className="font-semibold text-green-600 block md:inline">
                                  {formatCurrency(subscription.total_paid)}
                                </span>
                              </div>

                              {!subscription.is_expired && subscription.is_active && (
                                <div className="md:flex md:justify-between md:items-center">
                                  <span className="text-gray-500">Days Left:</span>
                                  <span className="font-semibold text-blue-600 block md:inline">
                                    {subscription.days_remaining} days
                                  </span>
                                </div>
                              )}
                            </div>

                            <div className="hidden md:block mt-4 pt-3 border-t border-gray-200">
                              <Button variant="link" size="sm" icon={Eye} fullWidth onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/company-subscriptions/view/${subscription.id}`);
                                }} className="bg-blue-50 hover:bg-blue-100 rounded-xl">
                                View Details
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Metadata */}
                  <div>
                    <h3 className="text-base sm:text-lg font-semibold text-gray-700 mb-4 pb-2 border-b-2 border-gray-200">System Information</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-6">
                      <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                        <p className="text-xs text-gray-500 mb-1">Created At</p>
                        <p className="text-sm font-semibold text-gray-900">{formatDate(company.created_at)}</p>
                      </div>

                      <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                        <p className="text-xs text-gray-500 mb-1">Updated At</p>
                        <p className="text-sm font-semibold text-gray-900">{formatDate(company.updated_at)}</p>
                      </div>

                      <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                        <p className="text-xs text-gray-500 mb-1">{t('common.createdBy')}</p>
                        <p className="text-sm font-semibold text-gray-900">{company.created_by || 'N/A'}</p>
                      </div>

                      <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                        <p className="text-xs text-gray-500 mb-1">Status</p>
                        <p className="text-sm font-semibold text-gray-900">
                          {company.is_active ? 'Active' : 'Inactive'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
                    <Button
                      variant="secondary"
                      icon={FileText}
                      onClick={handleDownloadInvoice}
                      disabled={invoicing || emailing}
                    >
                      {invoicing ? 'Invoice...' : 'Invoice'}
                    </Button>
                    <Button
                      variant="secondary"
                      icon={Mail}
                      onClick={handleEmailInvoice}
                      disabled={invoicing || emailing}
                    >
                      {emailing ? 'Emailing...' : 'Email Invoice'}
                    </Button>
                    <Button variant="primary" icon={Pencil} onClick={() => navigate(`/companies/edit/${companyId}`)}>
                      Edit Company
                    </Button>
                    <Button variant="secondary" icon={ArrowLeft} onClick={() => navigate('/companies')}>
                      Back to List
                    </Button>
                  </div>
                </div>
              </div>
      )}
    </DashboardLayout>
  );
};

export default ViewCompany;